// frontend_react/src/components/cart/CreateActivityPanel.jsx

import React, { useRef, useState } from "react";
import styled from "styled-components";
import {
  createActivity,
  importActivityProducts,
} from "../../services/activityApi";
import CompanyAutocomplete from "../common/CompanyAutocomplete";

const ACTIVITY_TYPES = [
  { value: "retail", label: "Retail / boutique" },
  { value: "restaurant", label: "Restaurant" },
  { value: "bar", label: "Bar / boissons" },
  { value: "evenementiel", label: "Évènementiel" },
  { value: "prestations_intellectuelles", label: "Prestations intellectuelles" },
  { value: "formation", label: "Formation" },
  { value: "location", label: "Location" },
  { value: "abonnement", label: "Abonnement" },
  { value: "billetterie", label: "Billetterie" },
  { value: "atelier", label: "Atelier" },
  { value: "service_sur_mesure", label: "Service sur mesure" },
  { value: "autre", label: "Autre" },
];

const IMPORT_TEMPLATE_HEADERS = [
  "designation",
  "sku",
  "barcode",
  "supplier_name",
  "supplier_reference",
  "purchase_price_ht",
  "purchase_price_ttc",
  "purchase_vat_rate",
  "selling_price_ht",
  "selling_price_ttc",
  "vat_rate",
  "stock_quantity",
  "unit",
];

export default function CreateActivityPanel({ onClose, onCreated }) {
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    activity_type: "retail",
    company_name: "",
    company_siret: "",
    company_uuid: "",
    description: "",
    default_vat_rate: "20",
  });

  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFiles = (newFiles) => {
    if (!newFiles?.length) return;

    const acceptedFiles = Array.from(newFiles).map((file) => ({
      id: crypto.randomUUID(),
      file,
      uploadedAt: new Date(),
    }));

    setFiles((prev) => [...prev, ...acceptedFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (id) => {
    if (busy) return;
    setFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setMessage("Le nom de l’activité est obligatoire.");
      setIsError(true);
      return false;
    }

    if (!formData.activity_type.trim()) {
      setMessage("Le type d’activité est obligatoire.");
      setIsError(true);
      return false;
    }

    if (!formData.company_name.trim() && !formData.company_uuid.trim()) {
      setMessage("Le rattachement à une entreprise est obligatoire.");
      setIsError(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (busy) return;
    if (!validateForm()) return;

    setBusy(true);
    setMessage("");
    setIsError(false);

    try {
      const payload = {
        name: formData.name.trim(),
        activity_type: formData.activity_type,
        company_name: formData.company_name.trim() || null,
        company_siret: formData.company_siret.trim() || null,
        company_uuid: null,
        description: formData.description.trim() || null,
        default_vat_rate: Number(formData.default_vat_rate || 20),
        metadata_json: {
          source: "cart_plus_tab",
          ai_import_reserved: true,
        },
      };

      const createRes = await createActivity(payload);

      const activity =
        createRes?.data?.activity ||
        createRes?.data ||
        null;

      const activityUuid =
        activity?.uuid ||
        createRes?.data?.activity_uuid ||
        createRes?.data?.uuid;

      if (!activityUuid) {
        throw new Error("UUID activité absent de la réponse backend.");
      }

      if (files.length > 0) {
        for (const item of files) {
          const formDataUpload = new FormData();
          formDataUpload.append("file", item.file);
          formDataUpload.append("ai_reserved", "true");

          await importActivityProducts(activityUuid, formDataUpload);
        }
      }

      onCreated?.({
        ...activity,
        uuid: activityUuid,
        activity_type: payload.activity_type,
        name: payload.name,
      });
    } catch (error) {
      console.error("Erreur création activité :", error);
      setMessage("Erreur lors de la création de l’activité ou de l’import produits.");
      setIsError(true);
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (date) =>
    date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleDownloadCsvTemplate = () => {
    const headers = IMPORT_TEMPLATE_HEADERS.join(";");

    const exampleRow = [
      "7 Wonders",
      "ASM001",
      "5425016920048",
      "Asmodee",
      "ASM-7WON",
      "20.00",
      "24.00",
      "20",
      "35.00",
      "42.00",
      "20",
      "5",
      "unité",
    ].join(";");

    const csvContent = `${headers}\n${exampleRow}\n`;
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", "template_import_activite_queens360.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  return (
    <Overlay onClick={busy ? undefined : onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Header>
          <div>
            <Eyebrow>Nouvelle caisse</Eyebrow>
            <h2>Créer une activité</h2>
            <p>
              Configure une activité, rattache-la à une entreprise, puis importe
              les produits ou prestations à vendre.
            </p>
          </div>

          <CloseButton type="button" onClick={onClose} disabled={busy}>
            ×
          </CloseButton>
        </Header>

        <Form onSubmit={handleSubmit}>
          {message && <PanelMessage $error={isError}>{message}</PanelMessage>}

          <Section>
            <SectionHeader>
              <Badge>Activité</Badge>
              <h4>Type & identification</h4>
            </SectionHeader>

            <FieldsGrid>
              <Field $full>
                <label htmlFor="name">Nom de l’activité *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ex : Boutique jeux, Restaurant, Évènements..."
                  required
                />
              </Field>

              <Field>
                <label htmlFor="activity_type">Type d’activité *</label>
                <select
                  id="activity_type"
                  name="activity_type"
                  value={formData.activity_type}
                  onChange={handleChange}
                  required
                >
                  {ACTIVITY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <label htmlFor="default_vat_rate">TVA par défaut (%)</label>
                <input
                  id="default_vat_rate"
                  name="default_vat_rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.default_vat_rate}
                  onChange={handleChange}
                />
              </Field>

              <Field $full>
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Description interne de l’activité..."
                />
              </Field>
            </FieldsGrid>
          </Section>

          <Section>
            <SectionHeader>
              <Badge>Société</Badge>
              <h4>Rattachement entreprise</h4>
            </SectionHeader>

            <FieldsGrid>
              <CompanyAutocomplete
                value={formData.company_name}
                siret={formData.company_siret}
                onChange={(companyData) =>
                  setFormData((prev) => ({
                    ...prev,
                    ...companyData,
                  }))
                }
              />
            </FieldsGrid>

            <InlineInfo>
              Le backend pourra résoudre l’entreprise par UUID ou par nom. À terme,
              ce champ pourra être remplacé par une recherche dynamique.
            </InlineInfo>
          </Section>

          <Section>
            <SectionHeader>
              <Badge>Produits</Badge>
              <h4>Import catalogue</h4>
            </SectionHeader>

            <DropZone
              onDrop={(event) => {
                event.preventDefault();
                if (!busy) handleFiles(event.dataTransfer.files);
              }}
              onDragOver={(event) => event.preventDefault()}
              onClick={() => {
                if (!busy) fileInputRef.current?.click();
              }}
            >
              <h3>{busy ? "Traitement en cours..." : "Importer un fichier"}</h3>
              <p>CSV, Excel, JSON — réservation IA prévue pour analyse future.</p>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                disabled={busy}
                accept=".csv,.xlsx,.xls,.json"
                onChange={(event) => handleFiles(event.target.files)}
              />
            </DropZone>

            <TemplateInfo>
              <h4>Template conseillé</h4>

              <p>
                Le fichier peut être en CSV, Excel ou JSON. Les colonnes ci-dessous sont
                recommandées pour alimenter correctement le catalogue, les prix d’achat,
                les prix de vente, le stock et les marges.
              </p>

              <TemplateButton
                type="button"
                onClick={handleDownloadCsvTemplate}
                disabled={busy}
              >
                Télécharger un modèle CSV
              </TemplateButton>

              <HeaderList>
                {IMPORT_TEMPLATE_HEADERS.map((header) => (
                  <HeaderBadge key={header}>{header}</HeaderBadge>
                ))}
              </HeaderList>

              <small>
                Les variantes françaises comme <strong>prix_achat_ht</strong>,{" "}
                <strong>prix_vente_ttc</strong>, <strong>fournisseur</strong> ou{" "}
                <strong>quantité</strong> sont aussi acceptées côté backend.
              </small>
            </TemplateInfo>

            <FilesPanel>
              <h4>Fichiers ajoutés</h4>

              {files.length === 0 && (
                <EmptyFile>Aucun fichier ajouté.</EmptyFile>
              )}

              {files.map((item) => (
                <FileRow key={item.id}>
                  <span>{item.file.name}</span>
                  <small>{formatDate(item.uploadedAt)}</small>

                  <button
                    type="button"
                    onClick={() => removeFile(item.id)}
                    disabled={busy}
                  >
                    ×
                  </button>
                </FileRow>
              ))}
            </FilesPanel>
          </Section>

          <Footer>
            <SecondaryButton type="button" onClick={onClose} disabled={busy}>
              Annuler
            </SecondaryButton>

            <SubmitButton type="submit" disabled={busy}>
              {busy ? "Création en cours..." : "Créer la caisse"}
            </SubmitButton>
          </Footer>
        </Form>
      </Panel>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.55);
`;

const Panel = styled.aside`
  width: min(680px, 100%);
  height: 100vh;
  overflow-y: auto;
  background: #0b1328;
  color: #f7f7f7;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.35);
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.4rem;
  background: #0b1328;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  h2 {
    margin: 0.25rem 0;
    font-size: 1.6rem;
    font-weight: 950;
  }

  p {
    margin: 0;
    opacity: 0.72;
    line-height: 1.45;
  }
`;

const Eyebrow = styled.span`
  display: inline-block;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 950;
`;

const CloseButton = styled.button`
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff9ea;
  font-size: 1.6rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 1rem;
  padding: 1.2rem;
`;

const Section = styled.section`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SectionHeader = styled.div`
  margin-bottom: 0.9rem;

  h4 {
    margin: 0.45rem 0 0;
    font-size: 1rem;
    font-weight: 950;
  }
`;

const Badge = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 950;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input,
  select,
  textarea {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  textarea {
    min-height: 90px;
    resize: vertical;
  }
`;

const InlineInfo = styled.div`
  margin-top: 0.9rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-size: 0.85rem;
  font-weight: 750;
  line-height: 1.4;
`;

const DropZone = styled.div`
  display: grid;
  place-items: center;
  min-height: 150px;
  padding: 1rem;
  border-radius: 18px;
  border: 2px dashed rgba(255, 249, 234, 0.45);
  background: rgba(255, 249, 234, 0.06);
  cursor: pointer;
  text-align: center;

  h3 {
    margin: 0;
    color: #fff9ea;
    font-weight: 950;
  }

  p {
    margin: 0.5rem 0 0;
    opacity: 0.72;
  }
`;

const FilesPanel = styled.div`
  margin-top: 1rem;

  h4 {
    margin: 0 0 0.75rem;
  }
`;

const EmptyFile = styled.p`
  margin: 0;
  opacity: 0.55;
`;

const FileRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  margin-top: 0.5rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);

  span {
    font-weight: 850;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    opacity: 0.65;
  }

  button {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 999px;
    background: rgba(180, 40, 40, 0.9);
    color: white;
    font-weight: 950;
    cursor: pointer;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 0.75rem;
  padding-bottom: 1rem;
`;

const SecondaryButton = styled.button`
  flex: 1;
  border: none;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: #fff9ea;
  font-weight: 950;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  flex: 2;
  border: none;
  border-radius: 12px;
  padding: 0.95rem 1rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PanelMessage = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const TemplateInfo = styled.div`
  margin-top: 1rem;
  padding: 0.9rem;
  border-radius: 14px;
  background: rgba(255, 249, 234, 0.08);
  border: 1px solid rgba(255, 249, 234, 0.14);

  h4 {
    margin: 0 0 0.5rem;
    color: #fff9ea;
    font-weight: 950;
  }

  p {
    margin: 0 0 0.75rem;
    opacity: 0.75;
    line-height: 1.45;
  }

  small {
    display: block;
    margin-top: 0.75rem;
    opacity: 0.7;
    line-height: 1.4;
  }
`;

const HeaderList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

const HeaderBadge = styled.span`
  padding: 0.35rem 0.55rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 900;
`;

const TemplateButton = styled.button`
  width: fit-content;
  border: none;
  border-radius: 999px;
  padding: 0.65rem 0.9rem;
  margin-bottom: 0.8rem;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.78rem;
  font-weight: 950;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;